'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Chevron, ProgressDots } from '@sprd/sprd-component-kit/v2';
import { Suspense, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import { HeroTeaserModule } from '@/types/amplience';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import DynamicHeading from '@/components/DynamicHeading/DynamicHeading';
import AmplienceVideoPlayer from '@/components/Video/AmplienceVideoPlayer';
import { getAmplienceImageUrl, getFallbackImage } from '@/utility/getImageUrl';
import { VIDEO_FORMATS } from '@/utility/getVideoUrl';
import AmplienceLink from '../Common/AmplienceLink';
import { PromoButton } from '../PromoButton/PromoButton';
export default function HeroTeaser(t0) {
  const $ = _c(50);
  const {
    content,
    className,
    isLazyLoadingOff
  } = t0;
  const t = useTranslations("slider");
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      loop: true
    };
    t2 = [Autoplay({
      delay: 5000,
      stopOnMouseEnter: true
    })];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t1, t2);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const hasMultipleItems = content.carouselItem.length > 1;
  const isMobileViewport = useMediaQuery("(max-width: 768px)");
  let t3;
  if ($[2] !== emblaApi) {
    t3 = () => {
      if (emblaApi) {
        emblaApi.scrollPrev();
      }
    };
    $[2] = emblaApi;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scrollPrev = t3;
  let t4;
  if ($[4] !== emblaApi) {
    t4 = () => {
      if (emblaApi) {
        emblaApi.scrollNext();
      }
    };
    $[4] = emblaApi;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const scrollNext = t4;
  let t5;
  let t6;
  if ($[6] !== emblaApi) {
    t5 = () => {
      if (!emblaApi) {
        return;
      }
      const setActiveSlide = () => {
        setActiveSlideIndex(emblaApi.selectedScrollSnap() || 0);
      };
      emblaApi.on("select", setActiveSlide);
      return () => {
        emblaApi.off("select", setActiveSlide);
      };
    };
    t6 = [emblaApi];
    $[6] = emblaApi;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== className) {
    t7 = clsx("h-[calc(100vh-145px)] max-h-[1000px] min-h-[222px] md:h-[calc(100vh-200px)] lg:h-[calc(100vh-204px)]", className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== content.carouselItem || $[12] !== isLazyLoadingOff || $[13] !== isMobileViewport) {
    let t9;
    if ($[15] !== isLazyLoadingOff || $[16] !== isMobileViewport) {
      t9 = (item, index) => {
        const desktopFallbackImage = getFallbackImage(item.videoOrImage.desktopVideo?.fallbackImg);
        const mobileFallbackImage = getFallbackImage(item.videoOrImage.mobileVideo?.fallbackImg);
        const useMobilePosterImage = isMobileViewport && !!mobileFallbackImage;
        const posterImage = useMobilePosterImage ? mobileFallbackImage : desktopFallbackImage;
        const useMobileVideoSource = isMobileViewport && !!item.videoOrImage.mobileVideo;
        const videoSource = useMobileVideoSource ? item.videoOrImage.mobileVideo : item.videoOrImage.desktopVideo;
        return <div className="relative size-full shrink-0 grow-0 basis-full" key={index}>{item.videoOrImage.formatSelection === "image" ? <HeroTeaserImage image={item.videoOrImage} loading={isLazyLoadingOff ? "eager" : "lazy"} priority={index === 0 ? "high" : "auto"} /> : <AmplienceVideoPlayer video={videoSource?.video} formats={useMobileVideoSource ? VIDEO_FORMATS.heroMobile : VIDEO_FORMATS.heroDesktop} altText={item.videoOrImage.videoAltText} transcriptUrl={item.videoOrImage.transcriptUrl} poster={posterImage ? getAmplienceImageUrl(posterImage.image, useMobilePosterImage ? {
            width: 800
          } : undefined).toString() : undefined} className="size-full object-cover" fallback={<HeroTeaserImage image={{
            image: desktopFallbackImage,
            mobileImage: mobileFallbackImage
          }} loading={isLazyLoadingOff ? "eager" : "lazy"} priority={index === 0 ? "high" : "auto"} />} />}<div className={`absolute bottom-0 z-10 mb-4 w-full p-4 sm:p-6 md:p-8 lg:p-16 xl:p-22.5 ${item.overlay.toLowerCase() == "black" ? "text-white" : "text-black"}`}><div className="md:max-w-139.25 lg:max-w-163 xl:max-w-180">{!item.subline && <h2 className="text-base font-bold tracking-wide sm:text-lg md:text-xl">{item.topLine}</h2>}<Suspense fallback={<h1 className="text-[1.5rem] font-bold tracking-wide break-words uppercase sm:text-[2rem] md:text-4xl lg:text-[2.5rem] xl:text-[2.75rem]">{item.headline}</h1>}><DynamicHeading className="text-[1.5rem] font-bold tracking-wide break-words uppercase sm:text-[2rem] md:text-4xl lg:text-[2.5rem] xl:text-[2.75rem]">{item.headline}</DynamicHeading></Suspense>{item.subline && <h2 className="text-base font-bold tracking-wide sm:text-lg md:text-xl">{item.topLine}</h2>}</div><div className={`${item.btnsAlignment === "vertical" ? "flex flex-col sm:flex-row sm:gap-4" : "flex gap-4"}`}>{item.ctas?.map(_temp)}</div></div><div className={`absolute right-0 bottom-0 left-0 size-full ${item.overlay.toLowerCase() == "black" ? "bg-gradient-to-b from-black/0 via-black/0 to-black/40" : "bg-gradient-to-b from-white/0 via-white/0 to-white/40"}`} /></div>;
      };
      $[15] = isLazyLoadingOff;
      $[16] = isMobileViewport;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = content.carouselItem.map(t9);
    $[11] = content.carouselItem;
    $[12] = isLazyLoadingOff;
    $[13] = isMobileViewport;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="flex size-full">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== emblaRef || $[21] !== t9) {
    t10 = <div className="size-full overflow-hidden" ref={emblaRef}>{t9}</div>;
    $[20] = emblaRef;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== hasMultipleItems || $[24] !== scrollNext || $[25] !== scrollPrev || $[26] !== t) {
    t11 = hasMultipleItems && <><button className="absolute top-auto right-[calc(2%+55px)] bottom-0 left-auto z-10 hidden size-10 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-black/30 p-2 text-white shadow-md hover:bg-black sm:flex md:size-10" aria-label={t("a11y.previousSlide")} onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        scrollPrev();
      }}><Chevron className="size-6 rotate-90" /></button><button className="absolute top-auto right-[2%] bottom-0 z-10 hidden size-10 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-black/30 p-2 text-white shadow-md hover:bg-black sm:flex md:size-10" aria-label={t("a11y.nextSlide")} onClick={event_0 => {
        event_0.preventDefault();
        event_0.stopPropagation();
        scrollNext();
      }}><Chevron className="size-6 rotate-270" /></button></>;
    $[23] = hasMultipleItems;
    $[24] = scrollNext;
    $[25] = scrollPrev;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = !hasMultipleItems && "hidden";
  let t13;
  if ($[28] !== t12) {
    t13 = clsx("absolute bottom-3 left-1/2 -translate-x-1/2 transform", t12);
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = content.carouselItem.length;
  let t15;
  if ($[30] !== emblaApi) {
    t15 = index_0 => emblaApi?.scrollTo(index_0);
    $[30] = emblaApi;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("a11y.progressDotsLabel");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t) {
    t17 = (index_1, total) => t("a11y.dotLabel", {
      slide: index_1 + 1,
      totalSlides: total
    });
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = {
      navigation: t16,
      dot: t17
    };
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== activeSlideIndex || $[40] !== content.carouselItem.length || $[41] !== t13 || $[42] !== t15 || $[43] !== t18) {
    t19 = <ProgressDots className={t13} activeIndex={activeSlideIndex} total={t14} onDotClick={t15} labels={t18} />;
    $[39] = activeSlideIndex;
    $[40] = content.carouselItem.length;
    $[41] = t13;
    $[42] = t15;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t19 || $[48] !== t7) {
    t20 = <div className={t7}>{t10}{t11}{t19}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t19;
    $[48] = t7;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
}
function _temp(cta, ctaIndex) {
  if ("cta" in cta) {
    return <Button size="l" key={ctaIndex} asChild={true} variant={cta.cta.buttonVariant || "primary"} className="mt-4 inline-block w-full bg-black px-6 py-3 font-bold text-white sm:w-fit md:mt-4"><AmplienceLink link={cta.cta} className="w-full">{cta.cta.label}</AmplienceLink></Button>;
  }
  const sharedButtonClass = clsx("mt-4 inline-block w-full bg-black px-6 py-3 font-bold sm:w-fit md:mt-4", cta.buttonVariant === "gradient-border" && "text-black");
  if ("promoButton" in cta) {
    return <PromoButton size="l" key={ctaIndex} variant={cta.buttonVariant} className={sharedButtonClass}>{cta.promoButton.label}</PromoButton>;
  } else {
    return <Button size="l" key={ctaIndex} variant={cta.buttonVariant || "primary"} className={sharedButtonClass} asChild={true}><AmplienceLink link={cta.link} className="w-full">{cta.link.label}</AmplienceLink></Button>;
  }
}
type HeroTeaserImageProps = {
  image: Pick<HeroTeaserModule['carouselItem'][0]['videoOrImage'], 'image' | 'mobileImage'>;
  loading: 'lazy' | 'eager';
  priority: 'high' | 'low' | 'auto';
};
const HeroTeaserImage = t0 => {
  const $ = _c(15);
  const {
    image: heroImage,
    loading,
    priority
  } = t0;
  const {
    mobileImage,
    image
  } = heroImage;
  let t1;
  if ($[0] !== image?.image) {
    t1 = image?.image ? getAmplienceImageUrl(image?.image).toString() : "";
    $[0] = image?.image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const desktopImageUrl = t1;
  let t2;
  if ($[2] !== mobileImage) {
    t2 = !!mobileImage && <source media="(max-width: 768px)" srcSet={getAmplienceImageUrl(mobileImage.image, {
      width: 800
    }).toString()} sizes="100vw" />;
    $[2] = mobileImage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== desktopImageUrl) {
    t3 = <source media="(min-width: 769px)" srcSet={desktopImageUrl} sizes="100vw" />;
    $[4] = desktopImageUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = image?.altText ?? "";
  let t5;
  if ($[6] !== desktopImageUrl || $[7] !== loading || $[8] !== priority || $[9] !== t4) {
    t5 = <img src={desktopImageUrl} alt={t4} className="size-full object-cover" loading={loading} fetchPriority={priority} />;
    $[6] = desktopImageUrl;
    $[7] = loading;
    $[8] = priority;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t5) {
    t6 = <picture>{t2}{t3}{t5}</picture>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};